import { Component, inject } from '@angular/core';
import { MatChipsModule } from '@angular/material/chips';
import { MatIcon } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import {
  CheckStageStatus,
  CheckStatus,
  ReworkStatus,
  stageStatusToReworkStatus
} from '../../../../../core/models/checks-model';
import { CheckService } from '../../../../../core/checks/check';
import { SnackbarService } from '../../../../../services/snackbar.service';
import { CheckStepperService } from '../../../services/check-stepper';
import { PendingCheckFacadeStore } from '../../store/pending-check-facade.store';
import { PendingCheckPermissionService } from '../../../services/pending-check-permission';
import { CheckImpossibleDialog } from '../../../dialogs/check-impossible-dialog/check-impossible-dialog';
import { ConfirmStatusDialog } from '../../../dialogs/confirm-status-dialog/confirm-status-dialog';
import { checkStatusToPhase } from '../../../../../core/common/utils/check-utils';

@Component({
  selector: 'app-check-header-bar',
  imports: [
    MatChipsModule,
    MatIcon,
    MatButtonModule,
  ],
  templateUrl: './check-header-bar.html',
  styleUrl: './check-header-bar.scss',
})
export class CheckHeaderBar {
  readonly data = inject(PendingCheckFacadeStore);
  readonly permission = inject(PendingCheckPermissionService);
  private readonly checkService = inject(CheckService);
  private readonly checkStepperService = inject(CheckStepperService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly dialog = inject(MatDialog);

  readonly CheckStatus = CheckStatus;
  readonly ReworkStatus = ReworkStatus;

  processCheck(): void {
    const currentCheck = this.data.currentCheck();
    const stageId = this.data.activeStageId();
    if (!currentCheck || stageId == null) return;

    const dialogRef = this.dialog.open(CheckImpossibleDialog, { data: {} });

    dialogRef.afterClosed().subscribe((commentText: string | undefined) => {
      if (!commentText?.trim()) return;

      this.data.addGeneralComment(commentText.trim(), 'CRITICAL');

      this.checkService.updateCheckStageApi(currentCheck.id, stageId, {
        status: 'CHECK_IMPOSSIBLE',
        phase: checkStatusToPhase(currentCheck.status),
      }).subscribe({
        next: (updatedStage) => {
          this.data.patchReworkStatusLocally(stageStatusToReworkStatus(updatedStage.status as CheckStageStatus));
          this.data.refreshCheck()
        },
        error: err => console.error('Stage-Update (CHECK_IMPOSSIBLE) fehlgeschlagen', err),
      });

      this.snackbarService.error('Prüfung nicht möglich', { message: commentText.trim() });
    });
  }

  confirmWiedervorlage(): void {
    const currentCheck = this.data.currentCheck();
    const stageId = this.data.activeStageId();
    if (!currentCheck || stageId == null) return;
    if (!this.permission.commentsResolved() && !this.permission.hasOnlyOwnUnresolvedComments()) return;

    const dialogRef = this.dialog.open(ConfirmStatusDialog, {
      data: {
        title: 'Wiedervorlage einleiten',
        message: 'Der Check wird an den Prüfer Kontrolle 1-1 zurückgegeben. Offene Punkte müssen geklärt werden.',
        targetStatus: 'Wiedervorlage',
      },
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (!confirmed) return;

      this.checkService.updateCheckStageApi(currentCheck.id, stageId, {
        status: 'REWORK_REQUIRED',
        phase: checkStatusToPhase(currentCheck.status),
      }).subscribe({
        next: (updatedStage) => {
          this.data.patchReworkStatusLocally(stageStatusToReworkStatus(updatedStage.status as CheckStageStatus));
          this.data.refreshCheck();
        },
        error: err => console.error('Stage-Update (REWORK_REQUIRED) fehlgeschlagen', err),
      });

      this.snackbarService.warning('In Wiedervorlage gesetzt');
    });
  }

  /**
   * Rückläufer erledigt – checker11 hat die Gründe bearbeitet.
   * Öffnet einen Bestätigungsdialog, setzt Stage-Status auf REVISED.
   */
  confirmResolveRework(): void {
    const currentCheck = this.data.currentCheck();
    const stageId = this.data.activeStageId();
    if (!currentCheck || stageId == null || !this.permission.canResolveRework()) return;

    const dialogRef = this.dialog.open(ConfirmStatusDialog, {
      data: {
        title: 'Rückläufer erledigt',
        message: 'Der Rework-Status wird aufgelöst und die Prüfung wird fortgesetzt.',
        targetStatus: CheckStatus[currentCheck.status],
      },
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (!confirmed) return;

      // Stage-Status auf REVISED setzen – API leitet daraus reworkStatus=None ab
      this.checkService.updateCheckStageApi(currentCheck.id, stageId, {
        status: 'REVISED',
        phase: checkStatusToPhase(currentCheck.status),
      }).subscribe({
        next: (updatedStage) => {
          this.data.patchReworkStatusLocally(stageStatusToReworkStatus(updatedStage.status as CheckStageStatus));
          this.data.refreshCheck();
        },
        error: err => console.error('Stage-Update (REVISED) fehlgeschlagen', err),
      });

      this.snackbarService.success('Rückläufer erledigt');
    });
  }

  confirmForwardCheck(): void {
    const currentCheck = this.data.currentCheck();
    const stageId = this.data.activeStageId();
    if (!currentCheck || stageId == null || !this.permission.canForwardCheck()) return;

    // isConsultant: Stepper-Checkbox ODER bereits gespeichertes Flag vom Backend
    const isConsultant = currentCheck.status === CheckStatus['Kontrolle 1-1']
      ? (this.checkStepperService.checkerIsConsultant() || currentCheck.isConsultant)
      : currentCheck.isConsultant;

    const nextStatus = this.checkStepperService.getNextStatus(currentCheck.status, isConsultant);
    if (nextStatus === currentCheck.status) return;

    // Bei Gutachterlicher Tätigkeit in K1-1: K1-2-Stage-ID für sequenziellen DONE-Call ermitteln.
    // K1-2 wird explizit als DONE geschlossen, damit deriveStatus() sauber K2 ableitet.
    const skipK12 = isConsultant && currentCheck.status === CheckStatus['Kontrolle 1-1'];
    const secondStageId = skipK12
      ? (this.data.currentStages().find(s => s.phase === 'SECOND')?.id ?? null)
      : null;

    const dialogRef = this.dialog.open(ConfirmStatusDialog, {
      data: {
        title: 'Kontrolle weitergeben',
        message: skipK12
          ? 'Gutachterliche Tätigkeit aktiv – Kontrolle 1-2 wird übersprungen.'
          : 'Möchten Sie die Kontrolle wirklich in den nächsten Status übergeben?',
        targetStatus: CheckStatus[nextStatus],
      },
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (!confirmed) return;

      // 1. K1-1 Stage als DONE abschließen
      this.checkService.updateCheckStageApi(currentCheck.id, stageId, {
        status: 'DONE',
        phase: checkStatusToPhase(currentCheck.status),
        completedAt: new Date().toISOString(),
      }).pipe(
        // 2. Gutachterliche Tätigkeit: K1-2 Stage sequenziell ebenfalls als DONE schließen,
        //    damit deriveStatus() direkt K2 ableitet (FIRST=DONE, SECOND=DONE, REVIEW=CREATED).
        switchMap(() => skipK12 && secondStageId != null
          ? this.checkService.updateCheckStageApi(currentCheck.id, secondStageId, {
              status: 'DONE',
              phase: 'SECOND',
              completedAt: new Date().toISOString(),
            })
          : of(null)
        ),
        // 3. Check-Level-Status auf nächste Phase setzen
        switchMap(() => this.checkService.updateCheckStatusApi(currentCheck.id, {
          status: nextStatus,
          checkerOid1:  currentCheck.checker11?.oid ?? undefined,
          checkerOid12: skipK12 ? null : (currentCheck.checker12?.oid ?? undefined),
          checkerOid2:  currentCheck.checker2?.oid  ?? undefined,
          dueDate1:  currentCheck.dueDate1  ? currentCheck.dueDate1.toISOString()  : undefined,
          dueDate12: skipK12 ? null : (currentCheck.dueDate12 ? currentCheck.dueDate12.toISOString() : undefined),
          dueDate2:  currentCheck.dueDate2  ? currentCheck.dueDate2.toISOString()  : undefined,
        })),
        // 4. isConsultant-Flag aktualisieren
        switchMap(() => this.checkService.updateCheckApi(currentCheck.id, { isConsultant })),
      ).subscribe({
        next: () => this.data.refreshCheck(),
        error: err => console.error('Weitergeben fehlgeschlagen', err),
      });

      this.snackbarService.success('Weitergegeben');
    });
  }
}


